import { AnimatePresence, motion } from 'motion/react';
import { Check, Globe } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/components/ui/utils';

const languages = [
  { code: 'pl', name: 'PL' },
  { code: 'en', name: 'EN' },
  { code: 'de', name: 'DE' },
] as const;

/**
 * Language picker, the same as the one in the trainer panel's sidebar: a quiet
 * globe + code button and a small raised menu with a check on the current one.
 * `placement` says where the menu opens (below in the navbar, above at the
 * bottom of a sheet).
 */
export function LanguageSwitch({
  placement = 'bottom',
  align = 'end',
  className,
}: {
  placement?: 'bottom' | 'top';
  /** which edge of the button the menu lines up with */
  align?: 'start' | 'end';
  className?: string;
}) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const actualLanguage = i18n.resolvedLanguage || i18n.language || 'pl';
  const currentLanguage = languages.find((lang) => actualLanguage.startsWith(lang.code)) || languages[0];

  // Close on outside click and on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setIsOpen(false);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem('i18nextLng', code);
    } catch {
      /* private mode: the change still applies to this visit */
    }
    setIsOpen(false);
  };

  const top = placement === 'top';

  return (
    <div ref={rootRef} className={cn('relative z-50', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Language"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={cn(
          'flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20',
          isOpen && 'bg-muted text-foreground',
        )}
      >
        <Globe size={16} strokeWidth={1.5} />
        <span className="font-mono text-xs">{currentLanguage.name}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: top ? 4 : -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.12 } }}
            transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            className={cn(
              'absolute z-50 w-28 rounded-xl surface-raised p-1',
              align === 'end' ? 'right-0' : 'left-0',
              top ? 'bottom-full mb-2' : 'top-full mt-2',
            )}
            style={{ transformOrigin: `${top ? 'bottom' : 'top'} ${align === 'end' ? 'right' : 'left'}` }}
          >
            {languages.map((lang) => {
              const active = actualLanguage.startsWith(lang.code);
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => handleLanguageChange(lang.code)}
                  className="flex h-8 w-full items-center justify-between rounded-lg px-2.5 text-foreground transition-colors hover:bg-muted"
                >
                  <span className="font-mono text-xs">{lang.name}</span>
                  {active && <Check size={14} strokeWidth={1.5} className="text-muted-foreground" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
